import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import ApiClient from '../../../state/api/ApiClient';
import PromiseBuilder from '../../../utils/PromiseUtils';
import Input from '@result/result-ui-components/src/components/Input';
import Button from '@result/result-ui-components/src/components/Button';
import { useForm } from '@result/result-ui-components/src/hooks/form/formHook';
import * as yup from 'yup';
import FormContainer from '../../widget/FormContainer';
import { useDispatch } from 'react-redux';
import {
	addErrorNotification,
	addSuccessNotification,
} from '../../../state/notification/notifications';
import { UserContext } from '../../../context/UserContext';

function ProfileForm() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { id } = useParams();
	const dispatch = useDispatch();
	const [isLoading, setIsLoading] = useState(false);
	const { user, setUser } = useContext(UserContext);

	useEffect(() => {
		if (user) {
			setValues(user);
		}
	}, [user]);

	const schema = {
		firstName: yup.string().required(),
		lastName: yup.string().required(),
		email: yup.string().email().required(),
		company: yup.string().optional(),
	};

	const createToast = (type = 'success') => {
		if (type === 'success') {
			dispatch(
				addSuccessNotification({
					title: t('update_success_title'),
					content: t('update_success_content'),
				})
			);
		} else {
			dispatch(
				addErrorNotification({
					title: t('update_fail_title'),
					content: t('update_fail_content'),
				})
			);
		}
	};

	const updateProfile = (url = '', key, cb = null) => {
		const request = async () => ApiClient.put(url, { [key]: values[key] });

		new PromiseBuilder()
			.then(request)
			.then(async () => {
				if (cb) {
					cb();
				} else {
					setUser({
						...user,
						...values,
					});
					setIsLoading(false);
					setFormNotDirty();
					createToast();
				}
			})
			.then(() => {})
			.catch(async () => {
				setIsLoading(false);
				createToast('error');
			})
			.run();
	};

	const submit = () => {
		setIsLoading(true);

		updateProfile(`/api/profile/${user.id}/firstname`, 'firstName', () => {
			updateProfile(`/api/profile/${user.id}/lastname`, 'lastName', () => {
				updateProfile(`/api/profile/${user.id}/email`, 'email', () => {
					updateProfile(`/api/profile/${user.id}/company`, 'company');
				});
			});
		});
	};

	const {
		values,
		getField,
		setFormNotDirty,
		handleSubmit,
		setValues,
		isEdited,
	} = useForm(submit, {}, schema);

	return (
		<>
			<FormContainer onSubmit={handleSubmit} isEdited={isEdited}>
				<div className="card">
					<header className="card-header">
						<p className="card-header-title">
							<span className="icon fz-user" />
							<span>{t('profile')}</span>
						</p>
					</header>
					<div className="card-content is-half-fullhd">
						<div className="columns flex-wrap">
							<div className="column is-6-tablet is-half-fullhd">
								<Input
									label={t('first_name')}
									formField={getField('firstName')}
								/>
								<Input
									label={t('last_name')}
									formField={getField('lastName')}
								/>
							</div>
							<div className="column is-6-tablet is-half-fullhd">
								<Input label={t('email')} formField={getField('email')} />
								<Input label={t('company')} formField={getField('company')} />
							</div>
						</div>
					</div>
				</div>

				<div className="form-footer">
					<Button
						label={t('back_to_start')}
						onClick={() => navigate('/' + t('navigation.href.map'))}
					/>
					<Button
						isSubmit={true}
						color="primary"
						label={t('save')}
						isLoading={isLoading}
					/>
				</div>
			</FormContainer>
		</>
	);
}

export default ProfileForm;
